import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserWeeklyBreakdown } from '@/api/query/useLeaderboard';
import { formatPrizeLabel } from '@/utils/prize';
import { EXPAND_PICKEMS_GRID } from './UserPickemsList';

interface WeeklyBreakdownListProps {
  userId: string;
  leagueCode?: string;
  /** When true, styles for white “my score” expand panel (dark text). */
  isCurrentUser?: boolean;
}

export const WeeklyBreakdownList: React.FC<WeeklyBreakdownListProps> = ({
  userId,
  leagueCode,
  isCurrentUser = false,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useUserWeeklyBreakdown(userId, leagueCode);

  const muted = isCurrentUser ? 'text-background/70' : 'text-muted-foreground';
  const primary = isCurrentUser ? 'text-background' : 'text-white';
  const success = isCurrentUser ? 'text-background' : 'text-success';
  const border = isCurrentUser ? 'border-black/10' : 'border-border/20';
  const empty = isCurrentUser ? 'text-background/40' : 'text-muted-foreground/40';

  if (isLoading) {
    return <div className={`text-sm font-sans ${muted}`}>{t('leaderboard.weekly.loading')}</div>;
  }

  if (error) {
    return <div className={`text-sm font-sans ${muted}`}>{t('leaderboard.weekly.failed')}</div>;
  }

  if (!data?.weeks || data.weeks.length === 0) {
    return <div className={`text-sm font-sans ${muted}`}>{t('leaderboard.weekly.empty')}</div>;
  }

  return (
    <div className="space-y-1">
      {data.weeks.map(week => {
        const prizeLabel = formatPrizeLabel(week.prize);
        const rank =
          week.rank !== null && week.rank !== undefined && Number(week.rank) > 0
            ? Number(week.rank)
            : null;

        return (
          <div
            key={week.week_number}
            className={`${EXPAND_PICKEMS_GRID} text-sm font-sans py-2 border-b ${border} last:border-0`}
          >
            <span className={`truncate ${primary}`}>{week.week_label}</span>
            <span className={`text-center ${muted}`}>
              {rank ?? <span className={empty}>-</span>}
            </span>
            <span className={`text-center font-semibold text-xs ${success}`}>
              {prizeLabel ?? <span className={empty}>-</span>}
            </span>
            <span className={`text-center font-medium ${primary}`}>{week.points}</span>
          </div>
        );
      })}
    </div>
  );
};
